/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-useless-escape */
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { useMemo, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { useSelector } from 'react-redux';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTable from 'components/base/AdvanceTable';
import { OutgoingResult } from 'data/outgoing';
import orderItemService from 'service/orderItem';
import orderOutService from 'service/orderOut';
import AcceptanceOfApplication from './AcceptanceOfApplication';
import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import orderService from 'service/order';
import './styles.scss';
import { useAppContext } from 'providers/AppProvider';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { useWindowWidth } from 'utils/useWindowSize';
import SupperModal from 'components/modals/SupperModal';
import ButtonRefresh from 'components/base/ButtonRefresh';

const OutgoingResultTable = () => {
  const { t, i18n } = useTranslation();
  const { pathname, state } = useLocation();
  const orderPid = Number(pathname.split('/').pop() || 0);
  const navigate = useNavigate();
  const { orders } = state;
  const {
    config: { theme }
  } = useAppContext();
  const windowSize: any = useWindowWidth();
  const url = process.env.REACT_APP_CDN_URL;
  const cashboxId = useSelector((state: any) => state?.shiftId?.shiftId);
  const cashboxPid = useSelector((state: any) => state?.shiftId?.shiftPid);

  const [canUpdate, setCanUpdate] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [changedOrderItems, setChangedOrderItems] = useState<any>([]);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isControl, setIsControl] = useState(false);
  const [disabledAccept, setDisabledAccept] = useState(false);
  const [openSupperModal, setOpenSupperModal] = useState(false);
  const [confirmToDelete, setConfirmToDelete] = useState(false);

  //Fetch Users By ID
  const { refetch: refetchOut } = useQuery(
    ['GET_ORDER_IN_CAN_UPDATE', orderPid],
    async () => {
      if (orderPid) {
        await orderOutService
          .getList({
            offset: 0,
            limit: 1000
          })
          .then((res: any) => {
            if (res) {
              const updatedRes = res?.orders?.find(
                (el: any) => +el?.pid === orderPid
              );

              setCanUpdate(updatedRes?.can_update ?? false);
              setIsControl(updatedRes?.is_control ?? false);
            }
          });
      }
    },
    { enabled: !!orderPid }
  );

  const { data, refetch } = useQuery(
    ['GET_ORDER_ITEM', orderPid],
    () => {
      if (orderPid !== 0) {
        const res = orderItemService
          .getOrderItemOutById(orderPid, {
            offset: 0,
            limit: 1000
          })
          .then((res: any) => {
            return res;
          });
        return res;
      }
    },
    { enabled: !!orderPid }
  );

  const orderBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: 'applications',
      url: '/applications/outgoing'
    },
    {
      label: 'outgoing',
      url: '/applications/outgoing'
    },
    {
      label: orders || '',
      active: true
    }
  ];

  useMemo(() => {
    const resultData =
      data?.order_items?.map((items: any) => {
        const packageQuantity = items?.package_quantity || 1;
        const quantity = items?.quantity_asked
          ? Number(items?.quantity_asked.toFixed(3))
          : 0;

        const fullPackages = Math.floor(quantity / packageQuantity);
        const remainingQuantity = quantity % packageQuantity;
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        return {
          package_quantity: items?.package_quantity,
          guid: items?.id,
          gupid: items?.pid,
          can_update_order_item: items?.can_update || false,
          currency: items?.currency?.name?.[i18n?.language],
          product_id: items?.product_id,
          product_pid: items?.product_pid,
          product_parent_id: items?.product_parent_id,
          product_parent_pid: items?.product_parent_pid,
          nomenclature_id: items?.nomenclature_id,
          nomenclature_pid: items?.nomenclature_pid,
          recipient_id: items?.recipient_id,
          recipient_pid: items?.recipient_pid,
          product_name: items?.nomenclature?.name,
          barcode: items?.nomenclature?.barcode,
          units: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
          measure_unit_id: items?.nomenclature?.measure_unit?.id,
          measure_unit_pid: items?.nomenclature?.measure_unit_pid,
          measure_unit_kind_pid: items?.nomenclature?.measure_unit_kind_pid,
          /* prettier-ignore */
          amount:
            items?.nomenclature?.measure_unit_kind_pid === 1
              ? quantityDisplay
              : items?.quantity_asked,
          status: items?.state_info?.name?.[i18n?.language],
          status_pid: items?.state_info?.pid,
          cost: items?.price_wholesale || 0,
          prevCost: items?.price_wholesale_org ?? 0,
          price_wholesale_org: items?.price_wholesale_org ?? 0,
          quantity_asked: items?.quantity_asked ?? 0,
          quantity_fact: items?.quantity_send ?? 0,
          quantity: items?.quantity_send ?? 0,
          quantity_accept: items?.proved_quantity_by_recipient ?? 0,
          discrepancies: items?.quantity_difference ?? 0,
          price_in: items?.price_in ?? 0,
          price: items?.price ?? 0,
          price_wholesale: items?.price_wholesale ?? 0,
          update_price: items?.discount?.percentage ? true : false,
          discount_percentage: items?.discount?.percentage ?? 0,
          amount_discrepancies: items?.price_difference || 0,
          proved_quantity_by_deliver: items?.proved_quantity_by_deliver,
          total_price: items?.total_price || 0,
          serial_number: items?.partion_codes,
          is_remove_table_selected: !items?.quantity_send ? true : false
        };
      }) ?? [];

    if (orderPid) {
      setChangedOrderItems(resultData);
    }

    return resultData;
  }, [data, orderPid, t, i18n]);

  const handleAcceptProduct = (id: any) => {
    if (id) {
      setDisabledAccept(true);
      orderItemService
        .updateOrderItemOut(id, {
          state: 6,
          cashbox_id: cashboxId,
          cashbox_pid: cashboxPid
        })
        .finally(() => {
          refetch();
          refetchOut();
          setDisabledAccept(false);
        });
    }
  };

  const outgoingResultTableColumns: ColumnDef<OutgoingResult>[] = [
    {
      accessorKey: 'order_number',
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'units',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'cost',
      header: t('wholesale_price'),
      cell: rowData => {
        const { cost } = rowData.row.original;

        return (
          <>
            {cost
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(cost)
                  .replace(/,/g, ' ')
              : 0}
          </>
        );
      },
      meta: {
        cellProps: {
          className: 'text-900',
          style: { textAlign: 'right' }
        }
      }
    },
    // {
    //   accessorKey: 'update_price',
    //   header: t('discount'),
    //   cell: rowData => {
    //     const { update_price } = rowData.row.original;

    //     // const handleCheckboxChange = (
    //     //   e: React.ChangeEvent<HTMLInputElement>,
    //     //   index: any
    //     // ) => {
    //     //   const checked = e.target.checked;
    //     //   setValue(checked);

    //     //   const updatedItems = changedOrderItems?.map((item: any, i: any) => {
    //     //     const cost = item?.cost ?? 0;

    //     //     const discountedCost =
    //     //       +discount_percentage > 0
    //     //         ? cost * (1 - +discount_percentage / 100)
    //     //         : cost;

    //     //     if (i === +index) {
    //     //       return {
    //     //         ...item,
    //     //         update_price: checked,
    //     //         cost: checked === true ? discountedCost : prevCost
    //     //       };
    //     //     }
    //     //     return item;
    //     //   });

    //     //   setChangedOrderItems(updatedItems);
    //     // };
    //     return (
    //       <div>
    //         <Form.Group className="printing-check">
    //           <Form.Check
    //             type="switch"
    //             checked={update_price}
    //             // onChange={e => handleCheckboxChange(e, rowData.row?.id)}
    //             className="cursor-pointer"
    //           />
    //         </Form.Group>
    //       </div>
    //     );
    //   },
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    {
      accessorKey: 'amount',
      header: t('application_quantity'),
      // cell: rowData => {
      //   const amount: any = rowData.getValue() ?? '0';

      //   const [value, setValue] = useState(amount);

      //   const updateProductInvoiceData = (
      //     name: string,
      //     value: string,
      //     index: any
      //   ) => {
      //     const updatedItems = changedOrderItems?.map((item: any, i: any) => {
      //       if (i === +index) {
      //         return { ...item, [name]: +value };
      //       }
      //       return item;
      //     });
      //     setChangedOrderItems(updatedItems);
      //   };

      //   const onBlur = (e: React.ChangeEvent<HTMLInputElement>, index: any) => {
      //     const { name, value } = e.target;
      //     updateProductInvoiceData(name, value, index);
      //   };

      //   return (
      //     <div>
      //       {!canUpdate ? (
      //         <Form.Group>
      //           <Form.Control
      //             type="text"
      //             style={{ width: '100px' }}
      //             name="amount"
      //             value={value}
      //             onChange={e => setValue(e.target.value)}
      //             onBlur={(e: any) => onBlur(e, rowData.row?.id)}
      //             onFocus={e => e.target.select()}
      //           />
      //         </Form.Group>
      //       ) : (
      //         amount
      //       )}
      //     </div>
      //   );
      // },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'quantity_fact',
      header: t('short_quantity_shipment'),
      cell: rowData => {
        const { package_quantity, measure_unit_kind_pid, quantity_fact } =
          rowData.row.original;

        const packageQuantity = package_quantity || 1;
        const quantity = quantity_fact ? Number(quantity_fact.toFixed(3)) : 0;

        const fullPackages = Math.floor(quantity / packageQuantity);
        const remainingQuantity = quantity % packageQuantity;
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        return (
          <div style={{ fontWeight: 'bold', color: 'red' }}>
            {measure_unit_kind_pid === 1 ? quantityDisplay : quantity_fact}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'quantity_accept',
      header: t('short_qunatity_accept'),
      cell: rowData => {
        const { package_quantity, measure_unit_kind_pid, quantity_accept } =
          rowData.row.original;

        const packageQuantity = package_quantity || 1;

        const quantity = quantity_accept
          ? Number(quantity_accept.toFixed(3))
          : 0;

        const fullPackages = Math.floor(quantity / packageQuantity);
        const remainingQuantity = quantity % packageQuantity;
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        return (
          <>{measure_unit_kind_pid === 1 ? quantityDisplay : quantity_accept}</>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'discrepancies',
      header: t('short_no_expoenses'),
      cell: rowData => {
        const { package_quantity, measure_unit_kind_pid, discrepancies } =
          rowData.row.original;

        const packageQuantity = package_quantity || 1;

        const quantity = discrepancies ? Number(discrepancies.toFixed(3)) : 0;

        const fullPackages = Math.floor(quantity / packageQuantity);
        const remainingQuantity = quantity % packageQuantity;
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        return (
          <>{measure_unit_kind_pid === 1 ? quantityDisplay : discrepancies}</>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'amount_discrepancies',
      header: t('asmount_discrepancy'),
      cell: rowData => {
        const { amount_discrepancies } = rowData.row.original;

        return (
          <>
            {amount_discrepancies
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(amount_discrepancies)
                  .replace(/,/g, ' ')
              : 0}
          </>
        );
      },
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },

    // {
    //   accessorKey: 'history',
    //   header: t('final_purchase_price'),
    //   meta: {
    //     cellProps: { className: 'text-900' }
    //   }
    // },

    {
      accessorKey: 'total_price',
      header: t('final_price'),
      cell: rowData => {
        const { total_price } = rowData.row.original;

        return (
          <>
            {total_price
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(total_price)
                  .replace(/,/g, ' ')
              : 0}
          </>
        );
      },
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },
    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status, status_pid } = rowData.row.original;

        let styles = { bg: '#e0f7fa', text: '#00796b' };

        if (status_pid === 10 || status_pid === 6 || status_pid === 4) {
          styles = { bg: '#fce4ec', text: '#c2185b' };
        } else if (status_pid === 1 || status_pid === 2) {
          styles = { bg: '#fffde7', text: '#fbc02d' };
        }

        return (
          <Button
            style={{
              backgroundColor: styles.bg,
              color: styles.text,
              whiteSpace: 'nowrap'
            }}
          >
            {status}
          </Button>
        );
      },
      meta: {
        cellProps: {
          className: 'text-900',
          style: { whiteSpace: 'nowrap' }
        }
      }
    },
    {
      accessorKey: 'accept_remainder',
      header: t('accept_remainder'),
      cell: rowData => {
        const { can_update_order_item } = rowData.row.original;

        return (
          <div
            style={{
              visibility: can_update_order_item === true ? 'visible' : 'hidden'
            }}
          >
            <Button
              disabled={disabledAccept}
              className="p-2"
              variant="primary"
              onClick={() => handleAcceptProduct(rowData?.row?.original?.gupid)}
            >
              {t('accept_remainder')}
            </Button>
          </div>
        );
      },
      meta: {
        cellProps: {
          className: 'text-900'
        }
      }
    }
  ];

  const table = useAdvanceTable({
    data: changedOrderItems,
    columns: outgoingResultTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleFinish = () => {
    const createData = changedOrderItems?.map((el: any) => {
      const quantity = el?.quantity_asked || '0';
      const quantityFact = el?.quantity_fact || '0';

      let integerPart = 0;
      let numerator = 0;
      let denominator = 0; // Default denominator to 1 if no fraction

      let integerPartFact = 0;
      let numeratorFact = 0;
      let denominatorFact = 0;

      const quantityStr = String(quantity);
      // Check if quantity contains '/'
      if (quantityStr?.includes('/')) {
        [numerator, denominator] = quantityStr.split('/').map(Number);

        integerPart = numerator; // Whole number part
        // eslint-disable-next-line
        denominator = denominator; // Remainder of the division
      } else {
        integerPart = +quantityStr;
      }

      const quantityStrFact = String(quantityFact);
      // Check if quantity contains '/'
      if (quantityStrFact?.includes('/')) {
        [numeratorFact, denominatorFact] = quantityStrFact
          .split('/')
          .map(Number);

        integerPartFact = numeratorFact; // Whole number part
        // eslint-disable-next-line
        denominatorFact = denominatorFact; // Remainder of the division
      } else {
        integerPartFact = +quantityStrFact;
      }

      // Package quantity
      const packageQuantity = +el?.package_quantity || 1;

      const adjustedQuantityFact =
        el?.measure_unit_kind_pid === 1
          ? integerPartFact * packageQuantity + denominatorFact
          : integerPartFact;
      const adjustedQuantity =
        el?.measure_unit_kind_pid === 1
          ? integerPart * packageQuantity + denominator
          : integerPart;

      return {
        barcode: el?.barcode,
        name: el?.product_name,
        quantity_asked:
          el?.measure_unit_kind_pid === 1
            ? +adjustedQuantity
            : +el?.quantity_asked,
        quantity_send:
          el?.measure_unit_kind_pid === 1
            ? +adjustedQuantityFact
            : +el?.quantity_fact,
        currency_pid: el?.currency_pid || 0,
        measure_unit_pid: el?.measure_unit_pid || 0,
        nomenclature_pid: el?.nomenclature_pid || 0,
        product_pid: el?.product_pid || 0,
        product_parent_pid: el?.product_parent_pid || 0,
        price: el?.price,
        price_in: el?.price_in,
        price_wholesale: el?.price_wholesale,
        pid: el?.gupid || 0,
        state: 4
      };
    });

    const updateData = {
      order_pid: orderPid || 0,
      order_items: createData
    };

    if (orderPid) {
      setOpenSupperModal(false);
      setConfirmToDelete(false);
      setFinishLoading(true);
      orderService.getOrderById(orderPid).then((res: any) => {
        if (res) {
          orderOutService
            ?.updateOrderOut(orderPid, {
              ...res,
              state: 4
            })
            .then((inresponse: any) => {
              if (inresponse) {
                orderItemService
                  .createOrderItem(updateData)
                  .then((createres: any) => {
                    if (createres) {
                      navigate('/applications/outgoing');
                    }
                  });
              }
            });
        }
      });
    }
  };

  const handleExcelById = () => {
    setLoading(true);
    const customFilename: string = 'Закуп по накладной.xlsx';

    // Fetch report and trigger download
    orderOutService
      ?.getOrderOutListExcel({ order_pid: orderPid, order_type: 'OUT' })
      .then((res: any) => {
        if (res?.filename !== '') {
          const resUrl = `${url}${res?.filename}`;
          const link = document.createElement('a');
          link.href = resUrl;
          link.download = customFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    if (confirmToDelete) {
      handleFinish();
    }
  }, [confirmToDelete, refetch]);

  return (
    <div>
      <PageBreadcrumb items={orderBreadcrumbItems} />

      <div>
        <div className="d-flex align-items-center justify-content-between gap-2 mb-5">
          <div className="d-flex align-items-center gap-2">
            <h2>{orders || ''}</h2>
            <ButtonRefresh onRefresh={refetch} />
          </div>
          {!canUpdate && (
            <Button
              className="d-flex align-items-center gap-2"
              style={{ height: '48px' }}
              variant="outline-primary"
              onClick={handleExcelById}
              loading={loading}
              loadingPosition="start"
            >
              <FeatherIcon icon="upload-cloud" size={20} />
              {windowSize > 768 && t('export')}
            </Button>
          )}
        </div>

        <AdvanceTableProvider {...table}>
          <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
            <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
            <AdvanceTableFooter pagination />
          </div>
        </AdvanceTableProvider>
        <div
          className={
            theme === 'dark' ? 'dark-footer-app-out' : 'footer-app-out'
          }
        >
          <div className="box">
            <p className="mb-0 text-bold">{t('total_amount')}:</p>
            <span>
              {data?.order?.total_order_amount
                ? new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                    useGrouping: true
                  })
                    .format(data?.order?.total_order_amount)
                    .replace(/,/g, ' ')
                : 0}
            </span>
          </div>

          <div className="main-btn-group">
            <Button
              disabled={orderPid === 0 || !canUpdate || finishLoading}
              variant="primary"
              onClick={() => setOpenModal(true)}
            >
              {t('accept')}
            </Button>
            <Button
              disabled={orderPid === 0 || !canUpdate || finishLoading}
              variant="danger"
              loading={finishLoading}
              onClick={() => setOpenSupperModal(true)}
              loadingPosition="start"
            >
              {t('reject')}
            </Button>
          </div>
        </div>
      </div>

      <SupperModal
        show={openSupperModal}
        setShow={setOpenSupperModal}
        setConfirm={setConfirmToDelete}
        title={t('reject')}
        bodyText={t('you_want_delete')}
      />

      <Modal
        show={openModal}
        onHide={() => setOpenModal(false)}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {t('acceptance_application')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AcceptanceOfApplication
            changedOrderItems={changedOrderItems}
            setChangedOrderItems={setChangedOrderItems}
            setOpenModal={setOpenModal}
            refetch={refetch}
            refetchOut={refetchOut}
            isControl={isControl}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OutgoingResultTable;
